import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { db, auth, COLLECTIONS } from '../firebase/config';import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  onSnapshot, 
  query,
  serverTimestamp 
} from 'firebase/firestore';
import { 
  Users, 
  Building2, 
  Settings, 
  Clock,
  Plus,
  Edit,
  Trash2,
  Shield,
  Calendar,
  Bell,
  Save,
  X
} from 'lucide-react';

const Admin = () => {
  const { currentUser, forceRefreshCurrentUser } = useAuth();
  const { setImpersonatedUser: setGlobalImpersonatedUser, updateUser, users: contextUsers, teams = [] } = useData();
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('users');
  // Users are now loaded from DataContext
  const [departments, setDepartments] = useState([]);
  const [systemSettings, setSystemSettings] = useState({
    defaultWeeklyCapacity: 40,
    acknowledgmentReminders: [4, 24], // hours
    statusUpdateCadence: 'daily', // 'daily' or 'weekly'
    workingDays: [0, 1, 2, 3, 4], // Sunday to Thursday
    workingHours: { start: '09:00', end: '18:00' }
  });

  // Modals
  const [showUserModal, setShowUserModal] = useState(false);
  const [showDepartmentModal, setShowDepartmentModal] = useState(false);
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [showNewProjectModal, setShowNewProjectModal] = useState(false);
  const [showNewTaskModal, setShowNewTaskModal] = useState(false);
  const [showImpersonateModal, setShowImpersonateModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [editingTeam, setEditingTeam] = useState(null);
  const [employeesDept, setEmployeesDept] = useState(null);
  
  // Impersonation state
  const [impersonatedUser, setLocalImpersonatedUser] = useState(null);
  
  // Get current effective user (either impersonated or real)
  const effectiveUser = impersonatedUser || currentUser;
  
  // Debug logging

  // Form states
  const [userForm, setUserForm] = useState({
    name: '',
    email: '',
    role: 'ceo',           // התפקיד הדיפולטיבי הוא מנכ"ל
    departmentId: '',
    teamIds: [],           // חדש - מערך מזהי צוותים (במקום teamId יחיד)
    teamId: '',            // שמור לתאימות לאחור
    teamManagerId: '',     // חדש - מזהה מנהל הצוות
    weeklyCapacity: 40,
    phone: '',
    isActive: true
  });

  const [departmentForm, setDepartmentForm] = useState({
    name: '',
    description: '',
    managerId: '',
    teamMembers: [],
    isActive: true
  });

  const [teamForm, setTeamForm] = useState({
    name: '',
    description: '',
    departmentId: '',
    managerId: '',
    isActive: true
  });

  const [newProjectForm, setNewProjectForm] = useState({
    name: '',
    description: '',
    scope: 'department',
    departmentId: '',
    startDate: '',
    endDate: '',
    teamMembers: [],
    managerId: ''
  });

  const [newTaskForm, setNewTaskForm] = useState({
    title: '',
    description: '',
    projectId: '',
    departmentId: '',
    assigneeId: '',
    managerId: '',
    priority: 'important_not_urgent',
    dueDate: '',
    estimate: 0,
    scope: 'department',
    teamMembers: []
  });

  // Check if user can manage users
  const canManageUsers = effectiveUser?.role === 'ceo' || effectiveUser?.role === 'org_admin' || effectiveUser?.role === 'deputy_ceo';
  
  // Impersonation functions
  const startImpersonation = (user) => {
    // Double-check permissions
    if (currentUser?.role !== 'ceo' && currentUser?.role !== 'org_admin' && currentUser?.role !== 'deputy_ceo') {
      console.error('Access denied: User is not ceo, org_admin, or deputy_ceo');
      alert('אין לך הרשאה לבצע פעולה זו');
      return;
    }
    
    setLocalImpersonatedUser(user);
    setGlobalImpersonatedUser(user);
    setShowImpersonateModal(false);
    console.log('Impersonating user:', user.name);
  };
  
  const stopImpersonation = () => {
    // Double-check permissions
    if (currentUser?.role !== 'ceo' && currentUser?.role !== 'org_admin' && currentUser?.role !== 'deputy_ceo') {
      console.error('Access denied: User is not ceo, org_admin, or deputy_ceo');
      return;
    }
    
    setLocalImpersonatedUser(null);
    setGlobalImpersonatedUser(null);
    console.log('Stopped impersonating user');
  };

  // Handle navigation state for active tab
  useEffect(() => {
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
      // If coming from departments page and should show department modal
      if (location.state.activeTab === 'departments' && location.state.showDepartmentModal) {
        setShowDepartmentModal(true);
        resetDepartmentForm();
        setEditingDepartment(null);
      }
    }
  }, [location.state]);

  // Keep activeTab as 'departments' when department modal is open
  useEffect(() => {
    if (showDepartmentModal && activeTab !== 'departments') {
      setActiveTab('departments');
    }
  }, [showDepartmentModal, activeTab]);

  // Close department modal when switching to other tabs (unless it was opened from navigation)
  useEffect(() => {
    if (showDepartmentModal && activeTab !== 'departments' && !location.state?.showDepartmentModal) {
      setShowDepartmentModal(false);
      setEditingDepartment(null);
      resetDepartmentForm();
    }
  }, [activeTab, showDepartmentModal, location.state?.showDepartmentModal]);

  // Clean up location state when component unmounts or location changes
  useEffect(() => {
    return () => {
      // Clear the navigation state to prevent issues on re-navigation
      if (location.state?.showDepartmentModal) {
        window.history.replaceState({}, document.title);
      }
    };
  }, [location.state?.showDepartmentModal]);

  useEffect(() => {
    if (!canManageUsers) return;

    const loadDepartments = () => {
      const deptsQuery = query(collection(db, COLLECTIONS.DEPARTMENTS));
      return onSnapshot(deptsQuery, (snapshot) => {
        const deptsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setDepartments(deptsData);
      });
    };

    const unsubscribeDepts = loadDepartments();

    return () => {
      unsubscribeDepts();
    };
  }, [canManageUsers]);

  if (!canManageUsers) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Shield className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">אין הרשאה</h3>
          <p className="text-gray-600">רק מנהל ארגון, מנכ"ל או סמנכ"ל יכול לגשת לדף זה</p>
        </div>
      </div>
    );
  }

const handleCreateUser = async (e) => {
  e.preventDefault();
  try {
    // בדוק אם יש סיסמה בטופס
    if (!userForm.password || userForm.password.length < 6) {
      alert('יש להזין סיסמה באורך של לפחות 6 תווים');
      return;
    }

    // יצירת משתמש ב-Firebase Auth
    const { createUserWithEmailAndPassword } = await import('firebase/auth');
    const userCredential = await createUserWithEmailAndPassword(
      auth, 
      userForm.email, 
      userForm.password
    );
    
    // יצירת משתמש ב-Firestore עם אותו ID
const { password, ...userDataWithoutPassword } = userForm; // מוציא את הסיסמה מהאובייקט
await addDoc(collection(db, COLLECTIONS.USERS), {
  ...userDataWithoutPassword, // משתמש בנתונים בלי הסיסמה
  // השתמש ב-UID מ-Auth כ-ID
  authUid: userCredential.user.uid,
  // Ensure teamIds is saved and teamId is set for backward compatibility
  teamIds: userForm.teamIds || [],
  teamId: userForm.teamIds.length > 0 ? userForm.teamIds[0] : '', 
  createdAt: serverTimestamp(),
  createdBy: currentUser.id
});
    
    setShowUserModal(false);
    resetUserForm();
    alert('המשתמש נוצר בהצלחה!');
  } catch (error) {
    console.error('Error creating user:', error);
    if (error.code === 'auth/email-already-in-use') {
      alert('כתובת האימייל כבר קיימת במערכת');
    } else if (error.code === 'auth/invalid-email') {
      alert('כתובת אימייל לא תקינה');
    } else if (error.code === 'auth/weak-password') {
      alert('הסיסמה חלשה מדי - יש להזין לפחות 6 תווים');
    } else {
      alert('שגיאה ביצירת המשתמש: ' + error.message);
    }
  }
};
  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      // Use updateUser from DataContext instead of direct Firebase update
      if (updateUser) {
        await updateUser(editingUser.id, {
          ...userForm,
          // Ensure teamIds is saved and teamId is set for backward compatibility
          teamIds: userForm.teamIds || [],
          teamId: userForm.teamIds.length > 0 ? userForm.teamIds[0] : '', // First team for backward compatibility
          updatedBy: currentUser.id
        });
        
        // If updating the current user, also refresh currentUser in AuthContext
        if (editingUser.id === currentUser.id) {
          // Force refresh current user data from Firebase
          if (forceRefreshCurrentUser) {
            await forceRefreshCurrentUser();
          }
        }
      }
      setShowUserModal(false);
      setEditingUser(null);
      resetUserForm();
    } catch (error) {
      console.error('Error updating user:', error);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('האם אתה בטוח שברצונך למחוק משתמש זה?')) {
      try {
        await deleteDoc(doc(db, COLLECTIONS.USERS, userId));
      } catch (error) {
        console.error('Error deleting user:', error);
      }
    }
  };

  const handleCreateDepartment = async (e) => {
    e.preventDefault();
    try {
      const departmentData = {
        name: departmentForm.name,
        description: departmentForm.description,
        managerId: departmentForm.managerId,
        teamMembers: departmentForm.teamMembers,
        isActive: departmentForm.isActive ?? true,
        createdBy: currentUser.id,
        createdAt: serverTimestamp()
      };
      
      await addDoc(collection(db, COLLECTIONS.DEPARTMENTS), departmentData);
      setShowDepartmentModal(false);
      resetDepartmentForm();
    } catch (error) {
      console.error('Error creating department:', error);
    }
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    try {
      const projectData = {
        ...newProjectForm,
        createdBy: currentUser.id,
        createdAt: serverTimestamp()
      };
      
      await addDoc(collection(db, COLLECTIONS.PROJECTS), projectData);
      setShowNewProjectModal(false);
      setNewProjectForm({
        name: '',
        description: '',
        scope: 'department',
        departmentId: '',
        startDate: '',
        endDate: '',
        teamMembers: [],
        managerId: ''
      });
    } catch (error) {
      console.error('Error creating project:', error);
    }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    try {
      const taskData = {
        ...newTaskForm,
        createdBy: currentUser.id,
        createdAt: serverTimestamp(),
        status: 'במסלול',
        acknowledged: false
      };
      
      await addDoc(collection(db, COLLECTIONS.TASKS), taskData);
      setShowNewTaskModal(false);
      setNewTaskForm({
        title: '',
        description: '',
        projectId: '',
        departmentId: '',
        assigneeId: '',
        managerId: '',
        priority: 'important_not_urgent',
        dueDate: '',
        estimate: 0,
        scope: 'department',
        teamMembers: []
      });
    } catch (error) {
      console.error('Error creating task:', error);
    }
  };

  const handleUpdateDepartment = async (e) => {
    e.preventDefault();
    try {
      await updateDoc(doc(db, COLLECTIONS.DEPARTMENTS, editingDepartment.id), {
        name: departmentForm.name,
        description: departmentForm.description,
        managerId: departmentForm.managerId,
        teamMembers: departmentForm.teamMembers,
        isActive: departmentForm.isActive,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.id
      });

      setShowDepartmentModal(false);
      setEditingDepartment(null);
      resetDepartmentForm();
    } catch (error) {
      console.error('Error updating department:', error);
    }
  };

  const handleDeleteDepartment = async (deptId) => {
    if (window.confirm('האם אתה בטוח שברצונך למחוק מחלקה זו?')) {
      try {
        await deleteDoc(doc(db, COLLECTIONS.DEPARTMENTS, deptId));
      } catch (error) {
        console.error('Error deleting department:', error);
      }
    }
  };

  const handleCreateTeam = async (e) => {
    e.preventDefault();
    try {
      const teamData = {
        ...teamForm,
        createdAt: serverTimestamp(),
        createdBy: effectiveUser.id
      };
      
      await addDoc(collection(db, COLLECTIONS.TEAMS), teamData);
      setShowTeamModal(false);
      resetTeamForm();
      alert('הצוות נוצר בהצלחה!');
    } catch (error) {
      console.error('Error creating team:', error);
      alert('שגיאה ביצירת הצוות');
    }
  };

  const handleUpdateTeam = async (e) => {
    e.preventDefault();
    try {
      const { id, ...updates } = editingTeam;
      await updateDoc(doc(db, COLLECTIONS.TEAMS, id), {
        ...updates,
        ...teamForm,
        updatedAt: serverTimestamp()
      });
      setShowTeamModal(false);
      setEditingTeam(null);
      resetTeamForm();
      alert('הצוות עודכן בהצלחה!');
    } catch (error) {
      console.error('Error updating team:', error);
      alert('שגיאה בעדכון הצוות');
    }
  };

  const handleDeleteTeam = async (teamId) => {
    if (window.confirm('האם אתה בטוח שברצונך למחוק צוות זה?')) {
      try {
        await deleteDoc(doc(db, COLLECTIONS.TEAMS, teamId));
        alert('הצוות נמחק בהצלחה!');
      } catch (error) {
        console.error('Error deleting team:', error);
        alert('שגיאה במחיקת הצוות');
      }
    }
  };

  const resetUserForm = () => {
    setUserForm({
      name: '',
      email: '',
      role: 'ceo',           // התפקיד הדיפולטיבי הוא מנכ"ל
      departmentId: '',
      teamIds: [],           // חדש - מערך מזהי צוותים
      teamId: '',            // שמור לתאימות לאחור
      teamManagerId: '',     // חדש - מזהה מנהל הצוות
      weeklyCapacity: 40,
      phone: '',
      isActive: true,
      password: ''
    });
  };

  const resetDepartmentForm = () => {
    setDepartmentForm({
      name: '',
      description: '',
      managerId: '',
      teamMembers: [],
      isActive: true
    });
  };

  const resetTeamForm = () => {
    setTeamForm({
      name: '',
      description: '',
      departmentId: '',
      managerId: '',
      isActive: true
    });
  };

  const openEditUser = (user) => {
    setEditingUser(user);
    setUserForm({
      name: user.name || '',
      email: user.email || '',
      role: user.role || 'ceo',
      departmentId: user.departmentId || '',
      teamIds: user.teamIds || (user.teamId ? [user.teamId] : []), // תמיכה במבנה החדש והישן
      teamId: user.teamId || '',           // שמור לתאימות לאחור
      teamManagerId: user.teamManagerId || '', // חדש - מזהה מנהל הצוות
      weeklyCapacity: user.weeklyCapacity || 40,
      phone: user.phone || '',
      isActive: user.isActive ?? true
    });
    setShowUserModal(true);
  };

  const openEditDepartment = (dept) => {
    setEditingDepartment(dept);
    setDepartmentForm({
      name: dept.name || '',
      description: dept.description || '',
      managerId: dept.managerId || '',
      teamMembers: dept.teamMembers || [],
      isActive: dept.isActive ?? true
    });
    setShowDepartmentModal(true);
  };

  const openEditTeam = (team) => {
    setEditingTeam(team);
    setTeamForm({
      name: team.name || '',
      description: team.description || '',
      departmentId: team.departmentId || '',
      managerId: team.managerId || '',
      isActive: team.isActive ?? true
    });
    setShowTeamModal(true);
  };

  const openEmployeesModal = (dept) => {
    setEmployeesDept(dept);
  };

  const tabs = [
    { id: 'users', label: 'משתמשים', icon: Users },
    { id: 'departments', label: 'מחלקות', icon: Building2 },
    { id: 'teams', label: 'צוותים', icon: Users },
    { id: 'settings', label: 'הגדרות מערכת', icon: Settings }
  ];

  return (
    <div className="p-4 md:p-6">
      {/* Header with impersonation controls */}
      <div className="flex flex-col lg:flex-row gap-4 lg:items-center lg:justify-between mb-6">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">
            {activeTab === 'users' && 'ניהול משתמשים'}
            {activeTab === 'departments' && 'ניהול מחלקות'}
            {activeTab === 'teams' && 'ניהול צוותים'}
            {activeTab === 'settings' && 'הגדרות מערכת'}
          </h1>
          <p className="text-gray-600 text-sm md:text-base">
            {activeTab === 'users' && 'ניהול משתמשי המערכת, תפקידים והרשאות'}
            {activeTab === 'departments' && 'ניהול מחלקות הארגון, מבנה ארגוני וצוותים'}
            {activeTab === 'teams' && 'ניהול צוותי העבודה, הקצאת עובדים וניהול פרויקטים'}
            {activeTab === 'settings' && 'הגדרות כלליות למערכת ופרמטרים ארגוניים'}
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Impersonation status */}
          {impersonatedUser && (
            <div className="flex items-center gap-2 bg-orange-100 border border-orange-300 rounded-lg px-3 py-2">
              <Shield className="w-4 h-4 text-orange-600" />
              <span className="text-sm text-orange-800">
                צופה בתור: {impersonatedUser.name}
              </span>
              <button
                onClick={stopImpersonation}
                className="text-orange-600 hover:text-orange-800 text-sm font-medium"
              >
                חזור למנהל
              </button>
            </div>
          )}
          
          {/* Action buttons for org_admin - removed as requested */}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex flex-wrap gap-2 sm:space-x-8" dir="rtl">
                     {tabs.map((tab) => (
             <button
               key={tab.id}
               onClick={() => {
                 // If department modal is open and trying to switch tabs, close it first
                 if (showDepartmentModal && tab.id !== 'departments') {
                   if (window.confirm('האם אתה בטוח שברצונך לסגור את הטופס להוספת מחלקה?')) {
                     setShowDepartmentModal(false);
                     setEditingDepartment(null);
                     resetDepartmentForm();
                     setActiveTab(tab.id);
                   }
                 } else if (showTeamModal && tab.id !== 'teams') {
                   // If team modal is open and trying to switch tabs, close it first
                   if (window.confirm('האם אתה בטוח שברצונך לסגור את הטופס להוספת צוות?')) {
                     setShowTeamModal(false);
                     setEditingTeam(null);
                     resetTeamForm();
                     setActiveTab(tab.id);
                   }
                 } else {
                   setActiveTab(tab.id);
                 }
               }}
               className={`${
                 activeTab === tab.id
                   ? 'border-blue-500 text-blue-600'
                   : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
               } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 space-x-reverse`}
               disabled={(showDepartmentModal && tab.id !== 'departments') || (showTeamModal && tab.id !== 'teams')}
             >
               <tab.icon className="w-4 h-4" />
               <span>{tab.label}</span>
             </button>
           ))}
        </nav>
      </div>

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-medium text-gray-900">רשימת משתמשים</h2>
            <button
              onClick={() => {
                resetUserForm();
                setEditingUser(null);
                setShowUserModal(true);
              }}
              className="bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 flex items-center space-x-2 space-x-reverse min-h-[44px] touch-manipulation"
            >
              <Plus className="w-4 h-4" />
              <span>הוסף משתמש</span>
            </button>
          </div>

          <div className="bg-white shadow rounded-lg overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    שם
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    אימייל
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    תפקיד
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    מחלקה
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    צוות
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    קיבולת שבועית
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    פעולות
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {contextUsers.map((user) => (
                  <tr key={user.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {user.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {user.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {(() => {
                        switch (user.role) {
                          case 'org_admin':
                            return 'מנהל ראשי';
                          case 'ceo':
                            return 'מנכ"ל';
                          case 'deputy_ceo':
                            return 'סמנכ"ל';
                          case 'dept_manager':
                            return 'מנהל מחלקה';
                          case 'team_manager':
                            return 'מנהל צוות';
                          case 'employee':
                            return 'עובד';
                          default:
                            return 'עובד';
                        }
                      })()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {departments.find(d => d.id === user.departmentId)?.name || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {teams.find(t => t.id === user.teamId)?.name || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {user.weeklyCapacity} שעות
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => openEditUser(user)}
                        className="text-blue-600 hover:text-blue-900 ml-3"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteUser(user.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Departments Tab */}
      {activeTab === 'departments' && (
        <div>
                     <div className="flex justify-between items-center mb-4">
             <div>
               <h2 className="text-lg font-medium text-gray-900">רשימת מחלקות</h2>
               <p className="text-sm text-gray-600 mt-1">כדי להוסיף מחלקה חדשה, עבור לדף המחלקות</p>
             </div>
             {/* Action buttons removed as requested */}
           </div>

          <div className="bg-white shadow rounded-lg overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    שם מחלקה
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    תיאור
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    מנהל
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    עובדים
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    צוותים
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    פעולות
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {departments.map((dept) => (
                  <tr 
                    key={dept.id}
                    onClick={() => navigate(`/departments/${dept.id}`)}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                    title="לחץ כדי לצפות בפרטי המחלקה"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {dept.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {dept.description || '-'}
                    </td>
                                         <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                       {dept.managerName || '-'}
                     </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {contextUsers.filter(u => u.departmentId === dept.id).length}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {teams.filter(t => t.departmentId === dept.id).length}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openEmployeesModal(dept);
                        }}
                        className="text-gray-600 hover:text-gray-900 ml-3"
                        title="הצג עובדים"
                      >
                        <Users className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditDepartment(dept);
                        }}
                        className="text-blue-600 hover:text-blue-900 ml-3"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteDepartment(dept.id);
                        }}
                        className="text-red-600 hover:text-red-900"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Teams Tab */}
      {activeTab === 'teams' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-medium text-gray-900">רשימת צוותים</h2>
            <button
              onClick={() => {
                resetTeamForm();
                setEditingTeam(null);
                setShowTeamModal(true);
              }}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center space-x-2 space-x-reverse"
            >
              <Plus className="w-4 h-4" />
              <span>הוסף צוות</span>
            </button>
          </div>

          <div className="bg-white shadow rounded-lg overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    שם צוות
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    תיאור
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    מחלקה
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    מנהל צוות
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    חברי צוות
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    סטטוס
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    פעולות
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {teams.map((team) => (
                  <tr key={team.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {team.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {team.description || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {departments.find(d => d.id === team.departmentId)?.name || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {contextUsers.find(u => u.id === team.managerId)?.name || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ({contextUsers.filter(u => u.teamId === team.id).length} עובדים)
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 rounded text-xs ${team.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {team.isActive ? 'פעיל' : 'לא פעיל'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => openEditTeam(team)}
                        className="text-blue-600 hover:text-blue-900 ml-3"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteTeam(team.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* System Settings Tab */}
      {activeTab === 'settings' && (
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-6">הגדרות מערכת</h2>
          
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                קיבולת שבועית ברירת מחדל (שעות)
              </label>
              <input
                type="number"
                value={systemSettings.defaultWeeklyCapacity}
                onChange={(e) => setSystemSettings({
                  ...systemSettings,
                  defaultWeeklyCapacity: parseInt(e.target.value)
                })}
                className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                שעות עבודה
              </label>
              <div className="flex items-center space-x-4 space-x-reverse">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">התחלה</label>
                  <input
                    type="time"
                    value={systemSettings.workingHours.start}
                    onChange={(e) => setSystemSettings({
                      ...systemSettings,
                      workingHours: { ...systemSettings.workingHours, start: e.target.value }
                    })}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">סיום</label>
                  <input
                    type="time"
                    value={systemSettings.workingHours.end}
                    onChange={(e) => setSystemSettings({
                      ...systemSettings,
                      workingHours: { ...systemSettings.workingHours, end: e.target.value }
                    })}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                תדירות תזכורות אישור (שעות)
              </label>
              <div className="flex items-center space-x-2 space-x-reverse">
                {systemSettings.acknowledgmentReminders.map((hours, index) => (
                  <input
                    key={index}
                    type="number"
                    value={hours}
                    onChange={(e) => {
                      const newReminders = [...systemSettings.acknowledgmentReminders];
                      newReminders[index] = parseInt(e.target.value);
                      setSystemSettings({
                        ...systemSettings,
                        acknowledgmentReminders: newReminders
                      });
                    }}
                    className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                  />
                ))}
              </div>
            </div>

            <div className="pt-4">
              <button
                onClick={() => {
                  // Save settings to Firebase
                  console.log('Saving system settings:', systemSettings);
                }}
                className="bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 flex items-center space-x-2 space-x-reverse min-h-[44px] touch-manipulation"
              >
                <Save className="w-4 h-4" />
                <span>שמור הגדרות</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User Modal */}
      {showUserModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {editingUser ? 'עריכת משתמש' : 'משתמש חדש'}
              </h3>
              <button
                onClick={() => {
                  setShowUserModal(false);
                  setEditingUser(null);
                  resetUserForm();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={editingUser ? handleUpdateUser : handleCreateUser}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    שם מלא
                  </label>
                  <input
                    type="text"
                    value={userForm.name}
                    onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    אימייל
                  </label>
                  <input
                    type="email"
                    value={userForm.email}
                    onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
{!editingUser && (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      סיסמה
    </label>
    <input
      type="password"
      value={userForm.password}
      onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
      required
      minLength={6}
    />
    <p className="text-xs text-gray-500 mt-1">
      לפחות 6 תווים
    </p>
  </div>
)}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    תפקיד
                  </label>
                  <select
                    value={userForm.role}
                    onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="ceo">מנכ"ל</option>
                    <option value="deputy_ceo">סמנכ"ל</option>
                    <option value="dept_manager">מנהל מחלקה</option>
                    <option value="team_manager">מנהל צוות</option>
                    <option value="employee">עובד</option>
                  </select>
                  {(effectiveUser?.role === 'ceo' || effectiveUser?.role === 'org_admin') && (
                    <p className="text-xs text-gray-500 mt-1">
                      רק מנכ"ל יכול ליצור מנהלים ראשיים נוספים
                    </p>
                  )}
                  {(effectiveUser?.role === 'deputy_ceo') && (
                    <p className="text-xs text-gray-500 mt-1">
                      כסמנכ"ל, אתה יכול ליצור ולנהל משתמשים במערכת
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    מחלקה
                  </label>
                  <select
                    value={userForm.departmentId}
                    onChange={(e) => setUserForm({ ...userForm, departmentId: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">בחר מחלקה</option>
                    {departments.map((dept) => (
                      <option key={dept.id} value={dept.id}>
                        {dept.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    צוותים (אופציונלי)
                  </label>
                  <div className="border border-gray-300 rounded-lg p-3 max-h-[120px] overflow-y-auto bg-gray-50">
                    {teams.filter(team => team.departmentId === userForm.departmentId).length > 0 ? (
                      teams.filter(team => team.departmentId === userForm.departmentId).map((team) => (
                        <label key={team.id} className="flex items-center gap-3 p-2 hover:bg-white rounded cursor-pointer">
                          <input
                            type="checkbox"
                            checked={userForm.teamIds.includes(team.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                // הוסף צוות
                                setUserForm(prevState => ({
                                  ...prevState,
                                  teamIds: [...prevState.teamIds, team.id]
                                }));
                              } else {
                                // הסר צוות
                                setUserForm(prevState => ({
                                  ...prevState,
                                  teamIds: prevState.teamIds.filter(id => id !== team.id)
                                }));
                              }
                            }}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <div className="flex-1">
                            <span className="text-sm font-medium text-gray-700">{team.name}</span>
                            {team.description && (
                              <p className="text-xs text-gray-500">{team.description}</p>
                            )}
                          </div>
                        </label>
                      ))
                    ) : (
                      <p className="text-sm text-gray-500 p-2">
                        {userForm.departmentId ? 'אין צוותים במחלקה זו' : 'בחר מחלקה כדי לראות צוותים'}
                      </p>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    ניתן לבחור מספר צוותים מהמחלקה שנבחרה
                  </p>
                </div>

                <div>
  <label className="block text-sm font-medium text-gray-700 mb-1">
    מנהל צוות (אופציונלי)
  </label>
  <select
    value={userForm.teamManagerId}
    onChange={(e) => setUserForm({ ...userForm, teamManagerId: e.target.value })}
    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
    disabled={!userForm.departmentId}  // שינוי כאן - תלוי במחלקה במקום בצוות
  >
    <option value="">בחר מנהל צוות</option>
    {contextUsers.filter(user => 
      (user.role === 'team_manager' || user.role === 'dept_manager') && 
      user.departmentId === userForm.departmentId &&
      user.isActive
    ).map((user) => (
      <option key={user.id} value={user.id}>
        {user.name} - {user.role === 'team_manager' ? 'מנהל צוות' : 'מנהל מחלקה'}
      </option>
    ))}
  </select>
  <p className="text-xs text-gray-500 mt-1">
    מנהלים זמינים מהמחלקה שנבחרה
  </p>
</div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    קיבולת שבועית (שעות)
                  </label>
                  <input
                    type="number"
                    value={userForm.weeklyCapacity}
                    onChange={(e) => setUserForm({ ...userForm, weeklyCapacity: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                    min="1"
                    max="80"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    טלפון (אופציונלי)
                  </label>
                  <input
                    type="tel"
                    value={userForm.phone}
                    onChange={(e) => setUserForm({ ...userForm, phone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 space-x-reverse mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowUserModal(false);
                    setEditingUser(null);
                    resetUserForm();
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
                >
                  בטל
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg"
                >
                  {editingUser ? 'עדכן' : 'צור'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Department Modal */}
      {showDepartmentModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {editingDepartment ? 'עריכת מחלקה' : 'מחלקה חדשה'}
              </h3>
              <button
                onClick={() => {
                  setShowDepartmentModal(false);
                  setEditingDepartment(null);
                  resetDepartmentForm();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={editingDepartment ? handleUpdateDepartment : handleCreateDepartment}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    שם מחלקה
                  </label>
                  <input
                    type="text"
                    value={departmentForm.name}
                    onChange={(e) => setDepartmentForm({ ...departmentForm, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    תיאור (אופציונלי)
                  </label>
                  <textarea
                    value={departmentForm.description}
                    onChange={(e) => setDepartmentForm({ ...departmentForm, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                    rows="3"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    מנהל מחלקה (אופציונלי)
                  </label>
                  <select
                    value={departmentForm.managerId || ''}
                    onChange={(e) => setDepartmentForm({ ...departmentForm, managerId: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">בחר מנהל</option>
                    {contextUsers.filter(user => user.role === 'dept_manager' || user.role === 'org_admin' || user.role === 'ceo' || user.role === 'deputy_ceo' || user.role === 'team_manager').map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.name} ({(() => {
                          switch (user.role) {
                            case 'org_admin':
                              return 'מנהל ראשי';
                            case 'ceo':
                              return 'מנכ"ל';
                            case 'deputy_ceo':
                              return 'סמנכ"ל';
                            case 'dept_manager':
                              return 'מנהל מחלקה';
                            case 'team_manager':
                              return 'מנהל צוות';
                            default:
                              return 'עובד';
                          }
                        })()})
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">בחר מנהל מתוך רשימת המנהלים במערכת</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    צוותים במחלקה (אופציונלי)
                  </label>
                  <div className="space-y-2">
                    {teams.filter(team => team.departmentId === (editingDepartment?.id || '')).map((team) => (
                      <div key={team.id} className="flex items-center gap-2 p-2 border rounded-lg">
                        <span className="text-sm">{team.name}</span>
                        <span className="text-xs text-gray-500">
                          ({contextUsers.filter(u => u.teamId === team.id).length} עובדים)
                        </span>
                      </div>
                    ))}
                    {teams.filter(team => team.departmentId === (editingDepartment?.id || '')).length === 0 && (
                      <p className="text-sm text-gray-500">אין צוותים במחלקה זו</p>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">צוותים נוצרים בנפרד ומוקצים למחלקות</p>
                </div>
              </div>

              <div className="flex justify-end space-x-3 space-x-reverse mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowDepartmentModal(false);
                    setEditingDepartment(null);
                    resetDepartmentForm();
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
                >
                  בטל
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg"
                >
                  {editingDepartment ? 'עדכן' : 'צור'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Team Modal */}
      {showTeamModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {editingTeam ? 'עריכת צוות' : 'צוות חדש'}
              </h3>
              <button
                onClick={() => {
                  setShowTeamModal(false);
                  setEditingTeam(null);
                  resetTeamForm();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={editingTeam ? handleUpdateTeam : handleCreateTeam}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    שם צוות
                  </label>
                  <input
                    type="text"
                    value={teamForm.name}
                    onChange={(e) => setTeamForm({ ...teamForm, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    תיאור (אופציונלי)
                  </label>
                  <textarea
                    value={teamForm.description}
                    onChange={(e) => setTeamForm({ ...teamForm, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                    rows="3"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    מחלקה
                  </label>
                  <select
                    value={teamForm.departmentId || ''}
                    onChange={(e) => setTeamForm({ ...teamForm, departmentId: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="">בחר מחלקה</option>
                    {departments.map((dept) => (
                      <option key={dept.id} value={dept.id}>
                        {dept.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    מנהל צוות (אופציונלי)
                  </label>
                  <select
                    value={teamForm.managerId || ''}
                    onChange={(e) => setTeamForm({ ...teamForm, managerId: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">בחר מנהל צוות</option>
                    {contextUsers.filter(user => user.role === 'team_manager' || user.role === 'dept_manager' || user.role === 'org_admin' || user.role === 'ceo' || user.role === 'deputy_ceo').map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.name} ({(() => {
                          switch (user.role) {
                            case 'org_admin':
                              return 'מנהל ראשי';
                            case 'ceo':
                              return 'מנכ"ל';
                            case 'deputy_ceo':
                              return 'סמנכ"ל';
                            case 'dept_manager':
                              return 'מנהל מחלקה';
                            case 'team_manager':
                              return 'מנהל צוות';
                            default:
                              return 'עובד';
                          }
                        })()})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    סטטוס פעילות
                  </label>
                  <select
                    value={teamForm.isActive}
                    onChange={(e) => setTeamForm({ ...teamForm, isActive: e.target.value === 'true' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value={true}>פעיל</option>
                    <option value={false}>לא פעיל</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end space-x-3 space-x-reverse mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowTeamModal(false);
                    setEditingTeam(null);
                    resetTeamForm();
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
                >
                  בטל
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg"
                >
                  {editingTeam ? 'עדכן' : 'צור'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Department Employees Modal */}
      {employeesDept && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                עובדי מחלקה – {employeesDept.name}
              </h3>
              <button
                onClick={() => setEmployeesDept(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-2 max-h-80 overflow-y-auto">
                              {contextUsers.filter(u => u.departmentId === employeesDept.id).length === 0 ? (
                <p className="text-sm text-gray-600">אין עובדים משויכים למחלקה זו.</p>
              ) : (
                contextUsers
                  .filter(u => u.departmentId === employeesDept.id)
                  .map((u) => (
                    <div key={u.id} className="border rounded-md p-2">
                      <div className="text-sm font-medium text-gray-900">{u.name}</div>
                      <div className="text-xs text-gray-600">{u.email}</div>
                      <div className="text-xs text-gray-600">
                        {(() => {
                          switch (u.role) {
                            case 'org_admin':
                              return 'מנהל ראשי';
                            case 'ceo':
                              return 'מנכ"ל';
                            case 'deputy_ceo':
                              return 'סמנכ"ל';
                            case 'dept_manager':
                              return 'מנהל מחלקה';
                            case 'team_manager':
                              return 'מנהל צוות';
                            case 'employee':
                              return 'עובד';
                            default:
                              return 'עובד';
                          }
                        })()}
                      </div>
                    </div>
                  ))
              )}
            </div>

            <div className="flex justify-end mt-4">
              <button
                type="button"
                onClick={() => setEmployeesDept(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
              >
                סגור
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Project Modal */}
      {showNewProjectModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">פרויקט חדש</h3>
              <button
                onClick={() => setShowNewProjectModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleCreateProject}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <input 
                    className="w-full px-3 py-2 border rounded" 
                    placeholder="שם הפרויקט" 
                    value={newProjectForm.name} 
                    onChange={(e)=>setNewProjectForm({...newProjectForm,name:e.target.value})}
                    required
                  />
                  <textarea 
                    className="w-full px-3 py-2 border rounded h-24" 
                    placeholder="תיאור הפרויקט" 
                    value={newProjectForm.description} 
                    onChange={(e)=>setNewProjectForm({...newProjectForm,description:e.target.value})}
                  />
                                     <select 
                     className="w-full px-3 py-2 border rounded"
                     value={newProjectForm.scope}
                     onChange={(e)=>setNewProjectForm({...newProjectForm,scope:e.target.value})}
                   >
                     <option value="personal">אישי</option>
                     <option value="department">מחלקתי</option>
                     <option value="organization">ארגוני</option>
                   </select>
                   
                   <select 
                     className="w-full px-3 py-2 border rounded"
                     value={newProjectForm.managerId}
                     onChange={(e)=>setNewProjectForm({...newProjectForm,managerId:e.target.value})}
                   >
                     <option value="">בחר מנהל פרויקט (אופציונלי)</option>
                     {contextUsers.filter(user => user.role === 'dept_manager' || user.role === 'org_admin' || user.role === 'ceo' || user.role === 'deputy_ceo' || user.role === 'team_manager').map(user => (
                       <option key={user.id} value={user.id}>
                         {user.name} ({(() => {
                           switch (user.role) {
                             case 'org_admin':
                               return 'מנהל ראשי';
                             case 'ceo':
                               return 'מנכ"ל';
                             case 'deputy_ceo':
                               return 'סמנכ"ל';
                             case 'dept_manager':
                               return 'מנהל מחלקה';
                             case 'team_manager':
                               return 'מנהל צוות';
                             default:
                               return 'עובד';
                           }
                         })()})
                       </option>
                     ))}
                   </select>
                </div>
                <div className="space-y-3">
                                     <select 
                     className="w-full px-3 py-2 border rounded"
                     value={newProjectForm.departmentId}
                     onChange={(e)=>setNewProjectForm({...newProjectForm,departmentId:e.target.value})}
                   >
                     <option value="">בחר מחלקה</option>
                     {departments.map(dept => (
                       <option key={dept.id} value={dept.id}>{dept.name}</option>
                     ))}
                   </select>
                   
                   <select 
                     className="w-full px-3 py-2 border rounded"
                     value={newProjectForm.teamId || ''}
                     onChange={(e)=>setNewProjectForm({...newProjectForm,teamId:e.target.value})}
                     disabled={!newProjectForm.departmentId}
                   >
                     <option value="">בחר צוות (אופציונלי)</option>
                     {teams.filter(team => team.departmentId === newProjectForm.departmentId).map(team => (
                       <option key={team.id} value={team.id}>{team.name}</option>
                     ))}
                   </select>
                  <input 
                    type="date" 
                    className="w-full px-3 py-2 border rounded" 
                    placeholder="תאריך התחלה" 
                    value={newProjectForm.startDate} 
                    onChange={(e)=>setNewProjectForm({...newProjectForm,startDate:e.target.value})} 
                  />
                                     <input 
                     type="date" 
                     className="w-full px-3 py-2 border rounded" 
                     placeholder="תאריך סיום" 
                     value={newProjectForm.endDate} 
                     onChange={(e)=>setNewProjectForm({...newProjectForm,endDate:e.target.value})} 
                   />
                   
                   <select 
                     multiple
                     className="w-full px-3 py-2 border rounded min-h-[100px]"
                     value={newProjectForm.teamMembers || []}
                     onChange={(e) => {
                       const values = Array.from(e.target.selectedOptions).map(o => o.value);
                       setNewProjectForm({...newProjectForm, teamMembers: values});
                     }}
                   >
                     <option value="" disabled>בחר חברי צוות (אופציונלי)</option>
                     {contextUsers.filter(user => user.role === 'employee' && user.isActive).map(user => (
                       <option key={user.id} value={user.id}>
                         {user.name} - {departments.find(d => d.id === user.departmentId)?.name || 'ללא מחלקה'}
                       </option>
                     ))}
                   </select>
                </div>
              </div>
              
              <div className="mt-6 flex justify-end gap-2">
                <button 
                  type="button"
                  className="px-4 py-2 text-gray-600 hover:text-gray-800" 
                  onClick={()=>setShowNewProjectModal(false)}
                >
                  בטל
                </button>
                <button 
                  type="submit"
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                >
                  צור פרויקט
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* New Task Modal */}
      {showNewTaskModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">משימה חדשה</h3>
              <button
                onClick={() => setShowNewTaskModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleCreateTask}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <input 
                    className="w-full px-3 py-2 border rounded" 
                    placeholder="כותרת המשימה" 
                    value={newTaskForm.title} 
                    onChange={(e)=>setNewTaskForm({...newTaskForm,title:e.target.value})}
                    required
                  />
                  <textarea 
                    className="w-full px-3 py-2 border rounded h-24" 
                    placeholder="תיאור המשימה" 
                    value={newTaskForm.description} 
                    onChange={(e)=>setNewTaskForm({...newTaskForm,description:e.target.value})}
                  />
                                     <select 
                     className="w-full px-3 py-2 border rounded"
                     value={newTaskForm.priority}
                     onChange={(e)=>setNewTaskForm({...newTaskForm,priority:e.target.value})}
                   >
                     <option value="important_urgent">חשוב ודחוף</option>
                     <option value="important_not_urgent">חשוב ולא דחוף</option>
                     <option value="not_important_urgent">דחוף ולא חשוב</option>
                     <option value="not_important_not_urgent">לא חשוב ולא דחוף</option>
                   </select>
                   
                   <select 
                     className="w-full px-3 py-2 border rounded"
                     value={newTaskForm.managerId}
                     onChange={(e)=>setNewTaskForm({...newTaskForm,managerId:e.target.value})}
                   >
                     <option value="">בחר מנהל פרויקט (אופציונלי)</option>
                     {contextUsers.filter(user => user.role === 'dept_manager' || user.role === 'org_admin' || user.role === 'ceo' || user.role === 'deputy_ceo' || user.role === 'team_manager').map(user => (
                       <option key={user.id} value={user.id}>
                         {user.name} ({(() => {
                           switch (user.role) {
                             case 'org_admin':
                               return 'מנהל ראשי';
                             case 'ceo':
                               return 'מנכ"ל';
                             case 'deputy_ceo':
                               return 'סמנכ"ל';
                             case 'dept_manager':
                               return 'מנהל מחלקה';
                             case 'team_manager':
                               return 'מנהל צוות';
                             default:
                               return 'עובד';
                           }
                         })()})
                       </option>
                     ))}
                   </select>
                </div>
                <div className="space-y-3">
                  <select 
                    className="w-full px-3 py-2 border rounded"
                    value={newTaskForm.departmentId}
                    onChange={(e)=>setNewTaskForm({...newTaskForm,departmentId:e.target.value})}
                  >
                    <option value="">בחר מחלקה</option>
                    {departments.map(dept => (
                      <option key={dept.id} value={dept.id}>{dept.name}</option>
                    ))}
                  </select>
                  
                  <select 
                    className="w-full px-3 py-2 border rounded"
                    value={newTaskForm.assigneeId}
                    onChange={(e)=>setNewTaskForm({...newTaskForm,assigneeId:e.target.value})}
                  >
                    <option value="">בחר מוקצה</option>
                    {contextUsers.filter(user => user.role === 'employee' && user.isActive).map(user => (
                      <option key={user.id} value={user.id}>
                        {user.name} - {departments.find(d => d.id === user.departmentId)?.name || 'ללא מחלקה'}
                      </option>
                    ))}
                  </select>
                  <input 
                    type="date" 
                    className="w-full px-3 py-2 border rounded" 
                    placeholder="דדליין" 
                    value={newTaskForm.dueDate} 
                    onChange={(e)=>setNewTaskForm({...newTaskForm,dueDate:e.target.value})} 
                  />
                                     <input 
                     type="number" 
                     className="w-full px-3 py-2 border rounded" 
                     placeholder="הערכת שעות" 
                     value={newTaskForm.estimate} 
                     onChange={(e)=>setNewTaskForm({...newTaskForm,estimate:parseInt(e.target.value) || 0})} 
                   />
                   
                   <select 
                     multiple
                     className="w-full px-3 py-2 border rounded min-h-[100px]"
                     value={newTaskForm.teamMembers || []}
                     onChange={(e) => {
                       const values = Array.from(e.target.selectedOptions).map(o => o.value);
                       setNewTaskForm({...newTaskForm, teamMembers: values});
                     }}
                   >
                     <option value="" disabled>בחר חברי צוות (אופציונלי)</option>
                     {contextUsers.filter(user => user.role === 'employee' && user.isActive).map(user => (
                       <option key={user.id} value={user.id}>
                         {user.name} - {departments.find(d => d.id === user.departmentId)?.name || 'ללא מחלקה'}
                       </option>
                     ))}
                   </select>
                </div>
              </div>
              
              <div className="mt-6 flex justify-end gap-2">
                <button 
                  type="button"
                  className="px-4 py-2 text-gray-600 hover:text-gray-800" 
                  onClick={()=>setShowNewTaskModal(false)}
                >
                  בטל
                </button>
                <button 
                  type="submit"
                  className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
                >
                  צור משימה
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Impersonate User Modal */}
              {showImpersonateModal && (currentUser?.role === 'ceo' || currentUser?.role === 'org_admin' || currentUser?.role === 'deputy_ceo') && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">התחבר בתור משתמש אחר</h3>
              <button
                onClick={() => setShowImpersonateModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <p className="text-sm text-gray-600 mb-4">
                בחר משתמש כדי לראות את הממשק כמו שהוא רואה אותו. אתה תישאר מחובר כמנהל הראשי.
              </p>

              {/* Users list */}
              <div className="space-y-3">
                <h4 className="font-medium text-gray-700">משתמשים זמינים:</h4>
                {contextUsers
                  .filter(user => user.isActive && user.id !== currentUser.id)
                  .map(user => (
                    <div key={user.id} className="flex items-center justify-between border rounded-lg p-3 hover:bg-gray-50">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <Shield className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium">{user.name}</p>
                          <p className="text-sm text-gray-600">{user.email}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`px-2 py-1 rounded text-xs ${
                              user.role === 'org_admin' || user.role === 'ceo' || user.role === 'deputy_ceo' ? 'bg-red-100 text-red-800' :
                              user.role === 'dept_manager' || user.role === 'team_manager' ? 'bg-blue-100 text-blue-800' :
                              'bg-green-100 text-green-800'
                            }`}>
                              {(() => {
                                switch (user.role) {
                                  case 'org_admin':
                                    return 'מנהל ראשי';
                                  case 'ceo':
                                    return 'מנכ"ל';
                                  case 'deputy_ceo':
                                    return 'סמנכ"ל';
                                  case 'dept_manager':
                                    return 'מנהל מחלקה';
                                  case 'team_manager':
                                    return 'מנהל צוות';
                                  case 'employee':
                                    return 'עובד';
                                  default:
                                    return 'עובד';
                                }
                              })()}
                            </span>
                            {user.departmentId && (
                              <span className="text-xs text-gray-500">
                                {departments.find(d => d.id === user.departmentId)?.name || 'לא ידוע'}
                              </span>
                            )}
                            {user.teamId && (
                              <span className="text-xs text-gray-500">
                                צוות: {teams.find(t => t.id === user.teamId)?.name || 'לא ידוע'}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => startImpersonation(user)}
                        className="px-4 py-2 bg-purple-600 text-white rounded text-sm hover:bg-purple-700"
                      >
                        התחבר בתורו
                      </button>
                    </div>
                  ))}
                
                {contextUsers.filter(user => user.isActive && user.id !== currentUser.id).length === 0 && (
                  <p className="text-gray-500 text-center py-4">
                    אין משתמשים זמינים להתחברות
                  </p>
                )}
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h5 className="font-medium text-blue-800 mb-2">מידע חשוב:</h5>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• אתה תישאר מחובר כמנהל הראשי</li>
                  <li>• תוכל לראות את הממשק כמו שהמשתמש רואה אותו</li>
                  <li>• תוכל לחזור למנהל הראשי בכל עת</li>
                  <li>• שינויים יישמרו בשם המשתמש המקורי</li>
                </ul>
              </div>
            </div>

            <div className="flex justify-end mt-6">
              <button
                onClick={() => setShowImpersonateModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
              >
                סגור
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;